import React from "react";
import { AlertTriangle, Check, Maximize2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";
import {
  CitationSnippet,
  type CitationGeometry
} from "../../../../components/common/citations";
import { useFormCitationDocument } from "../../context/FormCitationDocumentsContext";
import type { CasePrepUscisFieldProvenance } from "../../types";
import { formatAttributionTime } from "./attribution";
import { humanizeIdentifier } from "./fieldLine";
import { formatFieldValue } from "./fieldValue";
import {
  SOURCE_KINDS,
  sourceEntryOf,
  sourceKindOf,
  type FieldSourceKind
} from "./sourceKinds";
import type { FieldSourceItem, OpenFormCitation } from "./types";

type SourceEvidenceProps = {
  item: FieldSourceItem;
  /** `full` in the hover card; `source-row` collapses to the source row (the panel list). */
  variant?: "full" | "source-row";
  /** Set values in mono (IDs and numbers). */
  mono?: boolean;
  openCitationFieldName?: string | null;
  onToggleCitation?: (citation: OpenFormCitation) => void;
};

/** "Key fact: Current Employer" → "Current Employer"; else the key in words. */
function insightName(source: CasePrepUscisFieldProvenance): string {
  const label = String(source.source_label || "")
    .replace(/^key fact:\s*/i, "")
    .trim();
  if (label) return label;
  const key = String(source.key || "").trim();
  return key ? humanizeIdentifier(key) : "";
}

/** `attorney.last_name` → "Last Name"; `firm.name` → "Firm Name". */
export function profileKeyLabel(profileKey: string | null | undefined): string {
  const segments = String(profileKey || "")
    .split(".")
    .map((segment) => segment.replace(/\[\d+\]/g, ""))
    .filter(Boolean);
  if (!segments.length) return "";
  const last = segments[segments.length - 1];
  const parent = segments[segments.length - 2];
  const words =
    last === "name" && parent && parent !== "attorney"
      ? `${parent}_${last}`
      : last;
  return humanizeIdentifier(words);
}

function sourceName(
  source: CasePrepUscisFieldProvenance,
  kind: FieldSourceKind
): string {
  const label = String(source.source_label || "").trim();
  switch (kind) {
    case "intake":
      return label || "Intake";
    case "key_fact": {
      const name = insightName(source);
      return name ? `Case Insights · ${name}` : "Case Insights";
    }
    case "attorney_profile":
      return "Attorney Profile";
    case "pasted":
      return label || "Pasted Text";
    case "filing_rule":
      return "Case Filing Rules";
    default:
      return label || "Source Document";
  }
}

function sourceMeta(
  source: CasePrepUscisFieldProvenance,
  kind: FieldSourceKind
): string {
  if (kind === "document" && typeof source.citation?.page === "number") {
    return `p. ${source.citation.page}`;
  }
  if (kind === "pasted" && typeof source.step_index === "number") {
    return `Step ${source.step_index}`;
  }
  return "";
}

/** The first case-insensitive occurrence of `match` in `text`, marked. */
function Highlighted({
  text,
  match,
  className
}: {
  text: string;
  match: string;
  className: string;
}) {
  const needle = match.trim();
  const at = needle ? text.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className={cn("rounded-xs text-fg", className)}>
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/** "Q14" from the number the fill recorded; the question id is opaque. */
function questionNumber(number: unknown): string {
  const n = typeof number === "string" ? Number(number) : number;
  return typeof n === "number" && Number.isInteger(n) && n > 0 ? `Q${n}` : "";
}

function SourceRow({
  kind,
  name,
  meta,
  action
}: {
  kind: FieldSourceKind;
  name: string;
  meta: string;
  action?: React.ReactNode;
}) {
  const spec = SOURCE_KINDS[kind];
  const Icon = spec.icon;
  return (
    <div
      className="flex min-h-5 min-w-0 items-center gap-1.5"
      data-testid="field-source-row"
    >
      <Icon className={cn("size-3.5 shrink-0", spec.hue)} aria-hidden />
      <span className="min-w-0 truncate text-sm font-medium text-fg-2">
        {name}
      </span>
      {meta ? <span className="shrink-0 text-sm text-fg-4">{meta}</span> : null}
      {action ? <span className="ml-auto flex shrink-0">{action}</span> : null}
    </div>
  );
}

/**
 * Where a field's value came from, one body per source kind inside one quiet
 * block. A manual edit shows the source it replaced, then Source and On Form.
 *
 * A document crop needs a rendition, a polygon, a page and a page size to
 * scale against; while it downloads a 96px skeleton holds its place, and when
 * any piece is missing the block quotes the snippet, then shows the source row
 * alone.
 */
export function SourceEvidence({
  item,
  variant = "full",
  mono = false,
  openCitationFieldName = null,
  onToggleCitation
}: SourceEvidenceProps) {
  const { provenance } = item;
  const source = sourceEntryOf(provenance);
  const citation = source?.citation ?? null;
  const geometry: CitationGeometry = {
    polygon: citation?.polygon ?? null,
    pageWidth: citation?.page_width ?? null,
    pageHeight: citation?.page_height ?? null
  };
  // Only worth a fetch when every part of a highlight is present: a polygon with
  // no page size to scale against cannot be placed.
  const citable =
    Boolean(citation?.has_citation_pdf) &&
    Boolean(citation?.polygon?.length) &&
    typeof citation?.page === "number" &&
    Boolean(citation?.page_width) &&
    Boolean(citation?.page_height);
  const citationPdf = useFormCitationDocument(
    String(citation?.source_document_id || ""),
    citable
  );

  if (!provenance || !source) return null;

  const kind = sourceKindOf(source);
  const name = sourceName(source, kind);
  const canShowDocument = citable && citationPdf.status === "ready";
  const isOpen = openCitationFieldName === item.fieldName;
  const openCitation =
    canShowDocument && citationPdf.url && onToggleCitation
      ? () =>
          onToggleCitation({
            fieldName: item.fieldName,
            file: citationPdf.url as string,
            geometry,
            page: citation?.page as number,
            filename: name
          })
      : undefined;

  // The full body tags the question itself; the collapsed row carries it alone.
  const meta =
    variant === "source-row" && kind === "intake"
      ? questionNumber(source.source_metadata?.intake_question_number)
      : sourceMeta(source, kind);
  const sourceRow = (
    <SourceRow
      kind={kind}
      name={name}
      meta={meta}
      action={
        openCitation ? (
          <Button
            variant="ghost"
            size="xs"
            className="-my-0.5 -mr-1"
            onClick={openCitation}
            aria-pressed={isOpen}
          >
            {isOpen ? <X aria-hidden /> : <Maximize2 aria-hidden />}
            {isOpen ? "Hide Page" : "View Page"}
          </Button>
        ) : null
      }
    />
  );

  if (variant === "source-row") return sourceRow;

  const sourceValue = formatFieldValue(source.value ?? item.value);
  const metadata = source.source_metadata ?? {};
  let body: React.ReactNode = null;

  if (kind === "document") {
    if (canShowDocument && citationPdf.url) {
      body = (
        <CitationSnippet
          compact
          file={citationPdf.url}
          geometry={geometry}
          page={citation?.page as number}
          filename={name}
          isExpanded={isOpen}
          onExpand={openCitation ?? (() => undefined)}
        />
      );
    } else if (citable && citationPdf.status === "loading") {
      body = (
        <Skeleton
          className="h-24 w-full bg-fill-2"
          data-testid="field-source-crop-loading"
        />
      );
    } else if (citation?.reference_text) {
      body = (
        <p className="line-clamp-3 text-base text-fg-2">
          &ldquo;{citation.reference_text}&rdquo;
        </p>
      );
    }
  } else if (kind === "intake") {
    const question = String(metadata.intake_question || "").trim();
    const answer = String(metadata.intake_answer || "").trim();
    const number = questionNumber(metadata.intake_question_number);
    const answeredAt = formatAttributionTime(metadata.intake_answered_at, {
      withTime: false
    });
    if (question || answer) {
      body = (
        <>
          {question ? (
            <div className="flex min-w-0 items-start gap-1.5">
              {number ? <Tag mono>{number}</Tag> : null}
              <span className="min-w-0 pt-0.5 text-sm text-fg-3">
                {question}
              </span>
            </div>
          ) : null}
          {answer ? (
            <p className="whitespace-pre-wrap wrap-break-word text-base text-fg-2">
              <Highlighted
                text={answer}
                match={sourceValue}
                className="bg-purple-soft"
              />
            </p>
          ) : null}
          {answeredAt ? (
            <span className="text-sm text-fg-4">Answered {answeredAt}</span>
          ) : null}
        </>
      );
    }
  } else if (kind === "key_fact") {
    const text = String(source.text || "").trim();
    const confirmed =
      source.attorney_state === "confirmed" ||
      source.attorney_state === "overridden";
    if (text || confirmed) {
      body = (
        <>
          {text ? <p className="text-base text-fg-2">{text}</p> : null}
          {confirmed ? (
            <Tag>
              <Check className="text-success" aria-hidden />
              Confirmed by Attorney
            </Tag>
          ) : null}
        </>
      );
    }
  } else if (kind === "attorney_profile") {
    const key = profileKeyLabel(source.profile_key);
    if (key) body = <p className="text-base text-fg-2">{key}</p>;
  } else if (kind === "pasted") {
    const passage = String(
      source.source_free_text || metadata.free_text || ""
    ).trim();
    if (passage) {
      body = (
        <p className="line-clamp-4 whitespace-pre-wrap wrap-break-word text-base text-fg-2">
          <Highlighted
            text={passage}
            match={sourceValue}
            className="bg-warn-soft"
          />
        </p>
      );
    }
  } else if (kind === "filing_rule") {
    const rule = String(source.rule || "").trim();
    if (rule) body = <p className="text-base text-fg-2">{rule}</p>;
  }

  const isEdited = provenance.kind === "manual_edit";
  const caution = String(
    source.source_caution || provenance.source_caution || ""
  ).trim();

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-2 rounded-md bg-fill-1 p-2.5"
      data-testid="field-source-evidence"
    >
      {sourceRow}
      {body}
      {isEdited ? (
        <dl
          className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-line-1 pt-1.5 text-sm"
          data-testid="field-source-edit-grid"
        >
          <dt className="text-fg-3">Source</dt>
          <dd
            className={cn(
              "min-w-0 wrap-break-word text-fg-2",
              mono && "font-mono"
            )}
          >
            {sourceValue || "-"}
          </dd>
          <dt className="text-fg-3">On Form</dt>
          <dd
            className={cn(
              "min-w-0 wrap-break-word font-medium text-fg",
              mono && "font-mono"
            )}
          >
            {formatFieldValue(item.value) || "-"}
          </dd>
        </dl>
      ) : null}
      {caution ? (
        <span
          className="flex items-start gap-1.5 text-sm text-warn"
          data-testid="provenance-source-caution"
        >
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
          This source is {caution}. Check the value.
        </span>
      ) : null}
    </div>
  );
}
